import React from 'react';
import Image from 'next/image';
import { Link2Off, UserLock } from 'lucide-react';
import { css, cx } from 'styled-system/css';

import AppIcon from '@shaojiang/components/AppIcon';
import ShaojiangDate from '@shaojiang/components/ShaojiangDate';
import Tooltip from '@shaojiang/components/Tooltip';
import { Status } from '@shaojiang/types/common';
import { Project } from '@shaojiang/types/profile';

interface Props {
  project: Project;
  size?: 'sm' | 'md';
}

/** Footer row with project duration, tech stack icons and site status flags. */
export function ProjectCardActions({ project, size = 'md' }: Props) {
  const isSmall = size === 'sm';
  const duration = (
    <>
      <ShaojiangDate dateString={project.startDate} /> -{' '}
      <ShaojiangDate dateString={project.endDate} />
    </>
  );
  const siteMetaCount = Number(project.authRequired) + Number(project.status === Status.Inactive);
  const iconSize = isSmall ? 24 : 28;

  return (
    <div
      className={css({
        bg: 'bg.primarySubtle',
        py: '1',
        pl: '3',
        pr: '2',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '2',
      })}
    >
      <div className={css({ color: 'fg.muted', textStyle: 'sm' })}>
        {isSmall ? (
          <Tooltip title='Project duration'>
            <p>{duration}</p>
          </Tooltip>
        ) : (
          <>Duration: {duration}</>
        )}
      </div>
      <div className={css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap' })}>
        {project.tags.slice(0, isSmall ? 5 - siteMetaCount : project.tags.length).map((tag) => (
          <Tooltip key={tag} title={tag}>
            <Image
              alt={tag}
              width={iconSize}
              height={iconSize}
              src={`/icons/${tag.toLowerCase()}.svg`}
              className={css({ m: '1' })}
            />
          </Tooltip>
        ))}
        {siteMetaCount > 0 && (
          <div
            className={cx(
              css({ mx: '1', borderRightWidth: '1px', borderColor: 'primary.a6', h: '8' }),
              isSmall && css({ h: '6' }),
            )}
          />
        )}
        {project.authRequired && (
          <AppIcon
            icon={UserLock}
            size='lg'
            className={css({ mx: '1' })}
            tooltip='Authentication Required'
          />
        )}
        {project.status === Status.Inactive && (
          <AppIcon
            icon={Link2Off}
            size='lg'
            className={css({ mx: '1' })}
            tooltip='Website offline'
          />
        )}
      </div>
    </div>
  );
}
